import React from 'react';
import { formatMoney, useCurrency } from '../context/CurrencyContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from '../components/DatePicker';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../config';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PassportWidget from '../components/PassportWidget';
import {
  Calendar, User, MapPin, CreditCard, Clock, ChevronDown, ChevronUp,
  Loader2, ArrowLeft, AlertTriangle, X, Check, Pencil, Trash2,
  FileText, RefreshCw, MessageSquare, Send
} from 'lucide-react';
import { toast } from 'sonner';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var dateLocaleMap = { fr: 'fr-FR', en: 'en-US', jp: 'ja-JP' };

function formatYen(amount, cur) {
  return formatMoney(amount, cur);
}

var STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
  modified: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  completed: 'bg-zinc-600/30 text-zinc-300 border-zinc-500/30'
};

// Réservation "terminée" : confirmée/modifiée dont le dernier jour est passé (Tokyo).
function isBookingCompleted(b) {
  if (b.booking_status !== 'confirmed' && b.booking_status !== 'modified') return false;
  if (!b.start_date) return false;
  var todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tokyo' });
  var last = new Date(b.start_date + 'T00:00:00');
  last.setDate(last.getDate() + ((b.number_of_days || 1) - 1));
  var lastStr = last.getFullYear() + '-' + ('0' + (last.getMonth() + 1)).slice(-2) + '-' + ('0' + last.getDate()).slice(-2);
  return lastStr < todayStr;
}

/* ========== CANCEL MODAL ========== */
function CancelModal({ booking, onClose, onConfirmed }) {
  var { t } = useTranslation();
  var [step, setStep] = React.useState(1);
  var [cancelling, setCancelling] = React.useState(false);
  var [fees, setFees] = React.useState(null);
  var [loadingFees, setLoadingFees] = React.useState(true);

  React.useEffect(function() {
    axios.post(API_URL + '/api/bookings/v2/' + booking.booking_id + '/calculate-cancellation', {}, { withCredentials: true })
      .then(function(res) { setFees(res.data); setLoadingFees(false); })
      .catch(function(err) { toast.error(err.response?.data?.detail || t('travelerDash.error')); onClose(); });
  }, []);

  function confirmCancel() {
    setCancelling(true);
    axios.post(API_URL + '/api/bookings/v2/' + booking.booking_id + '/cancel', {}, { withCredentials: true })
      .then(function() {
        toast.success(t('travelerDash.cancel.success'));
        onConfirmed();
      })
      .catch(function(err) { toast.error(err.response?.data?.detail || t('travelerDash.error')); setCancelling(false); });
  }

  if (loadingFees) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-white/10 p-8 max-w-md w-full text-center">
          <Loader2 className="h-8 w-8 text-[#F9004D] animate-spin mx-auto" />
          <p className="text-zinc-400 mt-4">{t('travelerDash.calculatingFees')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="cancel-modal">
      <Card className="bg-zinc-900 border-red-500/30 p-6 sm:p-8 max-w-lg w-full">
        {step === 1 ? (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{t('travelerDash.cancel.title')}</h2>
                <p className="text-zinc-400 text-sm">{t('travelerDash.cancel.subtitle')}</p>
              </div>
            </div>

            <div className="bg-red-950/30 border border-red-500/20 rounded-xl p-5 mb-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">{t('travelerDash.cancel.amountPaid')}</span>
                <span className="text-white font-bold text-lg">{formatYen(fees.total_paid)}</span>
              </div>
              <div className="border-t border-red-500/20 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-red-400 font-bold">{t('travelerDash.cancel.fee', { percent: fees.cancellation_fee_percent })}</span>
                  <span className="text-red-400 font-bold text-lg">- {formatYen(fees.cancellation_fee)}</span>
                </div>
              </div>
              <div className="border-t border-red-500/20 pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-green-400 font-bold">{t('travelerDash.cancel.refunded')}</span>
                  <span className="text-green-400 font-bold text-xl">{formatYen(fees.refund_amount)}</span>
                </div>
              </div>
            </div>

            <p className="text-red-300 text-sm mb-6 p-3 bg-red-950/20 rounded-lg border border-red-500/10">
              {t('travelerDash.cancel.warning', { amount: formatYen(fees.cancellation_fee), percent: fees.cancellation_fee_percent })}
            </p>

            <div className="flex gap-3">
              <Button onClick={onClose} variant="outline" className="flex-1 border-white/20 text-zinc-300" data-testid="cancel-modal-back">
                {t('travelerDash.back')}
              </Button>
              <Button onClick={function() { setStep(2); }} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-modal-continue">
                {t('travelerDash.cancel.understand')}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-center mb-6">
              <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{t('travelerDash.cancel.finalTitle')}</h2>
              <p className="text-red-300 mt-2">
                {t('travelerDash.cancel.confirmQuestion', { amount: formatYen(fees.cancellation_fee) })}
              </p>
              <p className="text-zinc-400 text-sm mt-1">
                {t('travelerDash.cancel.refundLine', { refund: formatYen(fees.refund_amount), total: formatYen(fees.total_paid) })}
              </p>
            </div>

            <div className="flex gap-3">
              <Button onClick={function() { setStep(1); }} variant="outline" className="flex-1 border-white/20 text-zinc-300" data-testid="cancel-modal-go-back">
                {t('travelerDash.goBack')}
              </Button>
              <Button onClick={confirmCancel} disabled={cancelling} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-modal-confirm">
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                {t('travelerDash.cancel.confirmBtn')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ========== MODIFY MODAL ========== */
function ModifyModal({ booking, onClose, onConfirmed }) {
  var { t } = useTranslation();
  var cfg = useConfig();
  var [step, setStep] = React.useState(1);
  var [modifying, setModifying] = React.useState(false);
  var [fees, setFees] = React.useState(null);
  var [loadingFees, setLoadingFees] = React.useState(false);

  var [form, setForm] = React.useState({
    start_date: booking.start_date || '',
    number_of_days: booking.number_of_days || 1,
    participants: booking.participants || []
  });

  function addParticipant() {
    if (form.participants.length >= 4) { toast.error(t('travelerDash.modify.max4')); return; }
    var newP = form.participants.slice();
    newP.push({ name: '', rate_type: 'adult', rate_amount: 0 });
    setForm(Object.assign({}, form, { participants: newP }));
  }

  function removeParticipant(idx) {
    if (form.participants.length <= 1) { toast.error(t('travelerDash.modify.min1')); return; }
    var newP = [];
    for (var i = 0; i < form.participants.length; i++) {
      if (i !== idx) newP.push(form.participants[i]);
    }
    setForm(Object.assign({}, form, { participants: newP }));
  }

  function updateParticipant(idx, field, value) {
    var newP = form.participants.slice();
    newP[idx] = Object.assign({}, newP[idx]);
    newP[idx][field] = value;
    setForm(Object.assign({}, form, { participants: newP }));
  }

  function calculateFees() {
    setLoadingFees(true);
    var payload = {
      start_date: form.start_date,
      number_of_days: form.number_of_days,
      participants: []
    };
    for (var i = 0; i < form.participants.length; i++) {
      payload.participants.push({ name: form.participants[i].name, rate_type: form.participants[i].rate_type });
    }
    axios.post(API_URL + '/api/bookings/v2/' + booking.booking_id + '/calculate-modification', payload, { withCredentials: true })
      .then(function(res) { setFees(res.data); setStep(2); setLoadingFees(false); })
      .catch(function(err) { toast.error(err.response?.data?.detail || t('travelerDash.error')); setLoadingFees(false); });
  }

  function confirmModify() {
    setModifying(true);
    var payload = {
      start_date: form.start_date,
      number_of_days: form.number_of_days,
      participants: [],
      origin_url: window.location.origin
    };
    for (var i = 0; i < form.participants.length; i++) {
      payload.participants.push({ name: form.participants[i].name, rate_type: form.participants[i].rate_type });
    }
    axios.post(API_URL + '/api/bookings/v2/' + booking.booking_id + '/modify', payload, { withCredentials: true })
      .then(function(res) {
        if (res.data && res.data.requires_payment && res.data.checkout_url) {
          // Supplément à régler : redirection vers le paiement Stripe.
          window.location.href = res.data.checkout_url;
          return;
        }
        toast.success(t('travelerDash.modify.success'));
        onConfirmed();
      })
      .catch(function(err) { toast.error(err.response?.data?.detail || t('travelerDash.error')); setModifying(false); });
  }

  var participantRows = [];
  for (var i = 0; i < form.participants.length; i++) {
    var p = form.participants[i];
    var idx = i;
    participantRows.push(
      <div key={i} className="flex gap-2 items-center">
        <Input value={p.name} onChange={function(ii) { return function(e) { updateParticipant(ii, 'name', e.target.value); }; }(idx)}
          className="bg-black/20 border-white/10 text-white flex-1" placeholder={t('travelerDash.modify.namePlaceholder')} />
        <select value={p.rate_type} onChange={function(ii) { return function(e) { updateParticipant(ii, 'rate_type', e.target.value); }; }(idx)}
          className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-white">
          <option value="adult">{t('travelerDash.modify.adult')}</option>
          <option value="child">{t('travelerDash.modify.child')}</option>
        </select>
        <Button onClick={function(ii) { return function() { removeParticipant(ii); }; }(idx)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10">
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="modify-modal">
      <Card className="bg-zinc-900 border-blue-500/30 p-6 sm:p-8 max-w-lg w-full my-8">
        {step === 1 ? (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <Pencil className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{t('travelerDash.modify.title')}</h2>
                <p className="text-zinc-400 text-sm">{t('travelerDash.modify.subtitle', { percent: cfg.modification_fee_percent })}</p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-zinc-300 mb-1 block">{t('travelerDash.modify.dateStart')}</Label>
                <DatePicker value={form.start_date} minDate={new Date().toISOString().slice(0, 10)}
                  onChange={function(v) { setForm(Object.assign({}, form, { start_date: v })); }} />
              </div>
              <div>
                <Label className="text-zinc-300 mb-1 block">{t('travelerDash.modify.numberOfDays')}</Label>
                <Input type="number" value={form.number_of_days} min={1} max={7}
                  onChange={function(e) { setForm(Object.assign({}, form, { number_of_days: parseInt(e.target.value) || 1 })); }}
                  className="bg-black/20 border-white/10 text-white" />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-zinc-300">{t('travelerDash.modify.participants')}</Label>
                  <Button onClick={addParticipant} variant="outline" size="sm" className="border-white/20 text-zinc-300 text-xs" data-testid="add-participant-btn">
                    {t('travelerDash.modify.add')}
                  </Button>
                </div>
                <div className="space-y-2">{participantRows}</div>
              </div>
            </div>

            <p className="text-yellow-300 text-sm mb-4 p-3 bg-yellow-950/20 rounded-lg border border-yellow-500/10">
              {t('travelerDash.modify.warnLine', { percent: cfg.modification_fee_percent })}
            </p>

            <div className="flex gap-3">
              <Button onClick={onClose} variant="outline" className="flex-1 border-white/20 text-zinc-300" data-testid="modify-modal-cancel">
                {t('travelerDash.modify.cancelBtn')}
              </Button>
              <Button onClick={calculateFees} disabled={loadingFees} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="modify-modal-calculate">
                {loadingFees ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('travelerDash.modify.calcBtn')}
              </Button>
            </div>
          </div>
        ) : step === 2 ? (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="h-8 w-8 text-yellow-400" />
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{t('travelerDash.modify.feesTitle')}</h2>
            </div>

            <div className="bg-zinc-800/50 border border-white/10 rounded-xl p-5 mb-6 space-y-3">
              <div className="flex justify-between">
                <span className="text-zinc-400">{t('travelerDash.modify.initialPrice')}</span>
                <span className="text-white font-bold">{formatYen(fees.original_price)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">{t('travelerDash.modify.newPrice')}</span>
                <span className="text-white font-bold">{formatYen(fees.new_price)}</span>
              </div>
              <div className="border-t border-white/10 pt-3">
                <div className="flex justify-between">
                  <span className="text-yellow-400 font-bold">{t('travelerDash.modify.feeLine', { percent: fees.modification_fee_percent })}</span>
                  <span className="text-yellow-400 font-bold">{formatYen(fees.modification_fee)}</span>
                </div>
              </div>
              {fees.total_additional_charge > 0 && (
                <div className="flex justify-between">
                  <span className="text-red-400">{t('travelerDash.modify.surcharge')}</span>
                  <span className="text-red-400 font-bold">{formatYen(fees.total_additional_charge)}</span>
                </div>
              )}
              {fees.refund_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-green-400">{t('travelerDash.modify.refund')}</span>
                  <span className="text-green-400 font-bold">{formatYen(fees.refund_amount)}</span>
                </div>
              )}
            </div>

            <p className="text-yellow-300 text-sm mb-4 p-3 bg-yellow-950/20 rounded-lg border border-yellow-500/10">
              {t('travelerDash.modify.confirmFeesLine', { amount: formatYen(fees.modification_fee) })}
            </p>

            <div className="flex gap-3">
              <Button onClick={function() { setStep(1); }} variant="outline" className="flex-1 border-white/20 text-zinc-300" data-testid="modify-modal-back">
                {t('travelerDash.modify.editBtn')}
              </Button>
              <Button onClick={function() { setStep(3); }} className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white" data-testid="modify-modal-accept-fees">
                {t('travelerDash.modify.acceptFees')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{t('travelerDash.modify.finalTitle')}</h2>
            <p className="text-zinc-400 mb-2">
              {t('travelerDash.modify.finalQuestion', { amount: formatYen(fees.modification_fee) })}
            </p>
            <p className="text-zinc-500 text-sm mb-6">{t('travelerDash.modify.newAmount', { amount: formatYen(fees.new_price) })}</p>

            <div className="flex gap-3">
              <Button onClick={function() { setStep(2); }} variant="outline" className="flex-1 border-white/20 text-zinc-300" data-testid="modify-modal-go-back">
                {t('travelerDash.goBack')}
              </Button>
              <Button onClick={confirmModify} disabled={modifying} className="flex-1 bg-[#F9004D] hover:bg-[#D00040] text-white" data-testid="modify-modal-confirm">
                {modifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                {t('travelerDash.modify.confirmBtn')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ========== CHAT MODAL (voyageur ↔ guide) ========== */
function ChatModal({ booking, onClose }) {
  var { t } = useTranslation();
  var [conv, setConv] = React.useState(null);
  var [loading, setLoading] = React.useState(true);
  var [messages, setMessages] = React.useState([]);
  var [newMessage, setNewMessage] = React.useState('');
  var [sending, setSending] = React.useState(false);
  var bottomRef = React.useRef(null);

  function loadMessages(convId) {
    axios.get(API_URL + '/api/conversations/' + convId + '/messages', { withCredentials: true })
      .then(function(res) {
        setMessages(res.data);
        setTimeout(function() { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' }); }, 50);
      })
      .catch(function() { toast.error(t('travelerDash.chat.loadError')); });
  }

  React.useEffect(function() {
    axios.get(API_URL + '/api/conversations/me', { withCredentials: true })
      .then(function(res) {
        var match = null;
        for (var i = 0; i < res.data.length; i++) {
          if (res.data[i].booking_id === booking.booking_id) { match = res.data[i]; break; }
        }
        setConv(match);
        setLoading(false);
        if (match) loadMessages(match.conversation_id);
      })
      .catch(function() { setLoading(false); });
  }, [booking.booking_id]);

  function sendMsg() {
    if (!newMessage.trim() || !conv || sending) return;
    setSending(true);
    axios.post(API_URL + '/api/conversations/' + conv.conversation_id + '/messages', { content: newMessage }, { withCredentials: true })
      .then(function() { setNewMessage(''); loadMessages(conv.conversation_id); })
      .catch(function() { toast.error(t('travelerDash.error')); })
      .finally(function() { setSending(false); });
  }

  var msgList = [];
  for (var j = 0; j < messages.length; j++) {
    var m = messages[j];
    var isMe = m.sender_role === 'traveler';
    msgList.push(
      <div key={m.message_id || j} className={'flex ' + (isMe ? 'justify-end' : 'justify-start')}>
        <div className={'max-w-[80%] p-3 rounded-xl ' + (isMe ? 'bg-[#F9004D]/20 text-white' : 'bg-zinc-800 text-zinc-300')}>
          <p className="text-sm whitespace-pre-wrap">{m.content}</p>
          <p className="text-xs text-zinc-500 mt-1">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="chat-modal">
      <Card className="bg-zinc-900 border-white/10 w-full max-w-lg flex flex-col" style={{ maxHeight: '80vh' }} onClick={function(e) { e.stopPropagation(); }}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare className="h-5 w-5 text-[#00F0FF] flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-white font-bold truncate">{t('travelerDash.chat.title', { name: booking.guide_name || t('travelerDash.card.host') })}</h3>
              <p className="text-zinc-500 text-xs truncate">{booking.start_date}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white flex-shrink-0" data-testid="chat-close"><X className="h-5 w-5" /></button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 text-[#F9004D] animate-spin" /></div>
        ) : !conv ? (
          <p className="text-zinc-400 text-sm text-center p-8">{t('travelerDash.chat.notReady')}</p>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: '240px' }}>
              {msgList.length === 0 ? (
                <p className="text-zinc-500 text-sm text-center py-8">{t('travelerDash.chat.empty')}</p>
              ) : msgList}
              <div ref={bottomRef} />
            </div>
            <div className="p-4 border-t border-white/10 flex gap-2">
              <Input value={newMessage} onChange={function(e) { setNewMessage(e.target.value); }}
                onKeyDown={function(e) { if (e.key === 'Enter') sendMsg(); }}
                placeholder={t('travelerDash.chat.placeholder')} className="bg-black/20 border-white/10 text-white flex-1" data-testid="chat-input" />
              <Button onClick={sendMsg} disabled={sending || !newMessage.trim()} className="bg-[#F9004D] hover:bg-[#D00040] text-white" data-testid="chat-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ========== BOOKING DETAIL CARD ========== */
function BookingDetailCard({ booking, onRefresh }) {
  var { t, i18n } = useTranslation();
  var dateLocale = dateLocaleMap[i18n.language] || 'en-US';
  var [expanded, setExpanded] = React.useState(false);
  var [showCancel, setShowCancel] = React.useState(false);
  var [showModify, setShowModify] = React.useState(false);
  var [showChat, setShowChat] = React.useState(false);

  var completed = isBookingCompleted(booking);
  var status = completed ? 'completed' : (booking.booking_status || 'pending');
  var activityName = booking.activity_name && typeof booking.activity_name === 'object' ? (booking.activity_name[i18n.language] || booking.activity_name.fr || booking.activity_name.en) : (booking.activity_name || '');

  var canModify = status === 'confirmed' || status === 'modified';
  var canCancel = status === 'confirmed' || status === 'modified';
  // Messagerie encore possible sur une activité terminée (objet perdu, etc.).
  var canChat = (booking.booking_status === 'confirmed' || booking.booking_status === 'modified') && booking.payment_status === 'paid';

  var participantRows = [];
  if (booking.participants) {
    for (var i = 0; i < booking.participants.length; i++) {
      var p = booking.participants[i];
      participantRows.push(
        <div key={i} className="flex justify-between text-sm">
          <span className="text-zinc-300">{p.name}</span>
          <div className="flex items-center gap-2">
            <span className={p.rate_type === 'adult' ? 'text-[#00F0FF] text-xs' : 'text-[#D4AF37] text-xs'}>
              {p.rate_type === 'adult' ? t('travelerDash.modify.adult') : t('travelerDash.modify.child')}
            </span>
            <span className="text-white font-mono">{formatYen(p.rate_amount)}</span>
          </div>
        </div>
      );
    }
  }

  var historyRows = [];
  if (booking.action_history) {
    for (var j = 0; j < booking.action_history.length; j++) {
      var h = booking.action_history[j];
      var actionLabel = h.action === 'modification' ? t('travelerDash.history.modification') : (h.action === 'cancellation' || h.action === 'host_cancellation' || h.action === 'admin_refund') ? t('travelerDash.history.cancellation') : h.action;
      var actionColor = h.action === 'modification' ? 'text-blue-400' : 'text-red-400';
      historyRows.push(
        <div key={j} className="flex justify-between items-center text-sm py-2 border-b border-white/5 last:border-0">
          <div>
            <span className={'font-bold ' + actionColor}>{actionLabel}</span>
            <p className="text-zinc-500 text-xs">{h.timestamp ? new Date(h.timestamp).toLocaleString(dateLocale) : ''}</p>
            {h.details && <p className="text-zinc-500 text-xs">{h.details}</p>}
          </div>
          <div className="text-right">
            {h.modification_fee && <p className="text-yellow-400 text-xs">{t('travelerDash.history.fee')}: {formatYen(h.modification_fee)}</p>}
            {h.cancellation_fee && <p className="text-red-400 text-xs">{t('travelerDash.history.fee')}: {formatYen(h.cancellation_fee)}</p>}
            {h.refund_amount && <p className="text-green-400 text-xs">{t('travelerDash.history.refund')}: {formatYen(h.refund_amount)}</p>}
          </div>
        </div>
      );
    }
  }

  return (
    <div data-testid={'booking-card-' + booking.booking_id}>
      <Card className="bg-zinc-900/50 border-white/10 overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={'px-3 py-1 rounded-full text-xs font-bold border ' + (STATUS_COLORS[status] || STATUS_COLORS.pending)}>
                  {t('travelerDash.status.' + status, status)}
                </span>
                {booking.payment_status === 'paid' && (
                  <span className="px-3 py-1 rounded-full text-xs bg-green-500/10 text-green-400 border border-green-500/20">
                    <CreditCard className="h-3 w-3 inline mr-1" />{t('travelerDash.payment.paid')}
                  </span>
                )}
              </div>
              <h3 className="text-lg font-bold text-white">{activityName}</h3>
              {booking.activity_location && (
                <p className="text-zinc-400 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="h-4 w-4 text-[#F9004D]" /> {booking.activity_location}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#F9004D]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatYen(booking.total_price)}</p>
              {booking.total_fees_paid > 0 && (
                <p className="text-yellow-400 text-xs">{t('travelerDash.card.feesPaid', { amount: formatYen(booking.total_fees_paid) })}</p>
              )}
              {booking.refund_amount > 0 && (
                <p className="text-green-400 text-xs">{t('travelerDash.card.refunded', { amount: formatYen(booking.refund_amount) })}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-zinc-800/50 p-3 rounded-lg">
              <Calendar className="h-4 w-4 text-[#F9004D] mb-1" />
              <p className="text-xs text-zinc-500">{t('travelerDash.card.date')}</p>
              <p className="text-white text-sm font-bold">{booking.start_date}</p>
              {booking.activity_start_time && (
                <p className="text-[#00F0FF] text-xs mt-0.5">{booking.activity_start_time}{booking.activity_duration_hours ? ' · ' + t('travelerDash.card.durationHours', { count: booking.activity_duration_hours }) : ''}</p>
              )}
            </div>
            <div className="bg-zinc-800/50 p-3 rounded-lg">
              <Clock className="h-4 w-4 text-[#00F0FF] mb-1" />
              <p className="text-xs text-zinc-500">{t('travelerDash.card.duration')}</p>
              <p className="text-white text-sm font-bold">{t('travelerDash.card.days', { count: booking.number_of_days })}</p>
            </div>
            <div className="bg-zinc-800/50 p-3 rounded-lg">
              <User className="h-4 w-4 text-[#D4AF37] mb-1" />
              <p className="text-xs text-zinc-500">{t('travelerDash.card.participants')}</p>
              <p className="text-white text-sm font-bold">{booking.participants?.length || 0}</p>
            </div>
            <div className="bg-zinc-800/50 p-3 rounded-lg">
              <FileText className="h-4 w-4 text-zinc-400 mb-1" />
              <p className="text-xs text-zinc-500">{t('travelerDash.card.ref')}</p>
              <p className="text-white text-sm font-mono">{booking.booking_id?.slice(-8)}</p>
            </div>
          </div>

          {/* Point de rendez-vous + itinéraire Google Maps */}
          {booking.activity_meeting_point && (status === 'confirmed' || status === 'modified') && (
            <div className="bg-[#00F0FF]/5 border border-[#00F0FF]/20 rounded-lg p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <MapPin className="h-4 w-4 text-[#00F0FF] mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-zinc-500">{t('travelerDash.card.meetingPoint')}</p>
                  <p className="text-white text-sm">{booking.activity_meeting_point}</p>
                </div>
              </div>
              <a href={'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(booking.activity_meeting_point)}
                target="_blank" rel="noreferrer" className="flex-shrink-0" data-testid={'directions-' + booking.booking_id}>
                <Button variant="outline" size="sm" className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 w-full sm:w-auto">
                  <MapPin className="h-4 w-4 mr-1" /> {t('travelerDash.card.directions')}
                </Button>
              </a>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button onClick={function() { setExpanded(!expanded); }} variant="outline" size="sm" className="border-white/20 text-zinc-300 hover:bg-white/5" data-testid={'expand-booking-' + booking.booking_id}>
              {expanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
              {expanded ? t('travelerDash.card.hide') : t('travelerDash.card.details')}
            </Button>
            {canChat && (
              <Button onClick={function() { setShowChat(true); }} variant="outline" size="sm" className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10" data-testid={'chat-booking-' + booking.booking_id}>
                <MessageSquare className="h-4 w-4 mr-1" /> {t('travelerDash.card.contactHost')}
              </Button>
            )}
            {canModify && (
              <Button onClick={function() { setShowModify(true); }} variant="outline" size="sm" className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid={'modify-booking-' + booking.booking_id}>
                <Pencil className="h-4 w-4 mr-1" /> {t('travelerDash.card.modify')}
              </Button>
            )}
            {canCancel && (
              <Button onClick={function() { setShowCancel(true); }} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={'cancel-booking-' + booking.booking_id}>
                <Trash2 className="h-4 w-4 mr-1" /> {t('travelerDash.card.cancel')}
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="border-t border-white/10 p-5 sm:p-6 space-y-6">
            {/* Facture / Invoice */}
            <div>
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#F9004D]" /> {t('travelerDash.card.invoice')}
              </h4>
              <div className="bg-zinc-800/30 rounded-lg p-4 space-y-2">
                {participantRows}
                <div className="border-t border-white/10 pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">{t('travelerDash.card.subtotalPerDay')}</span>
                    <span className="text-white font-mono">{formatYen(booking.subtotal_per_day)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">{t('travelerDash.card.timesDays', { count: booking.number_of_days })}</span>
                    <span className="text-white font-bold font-mono">{formatYen(booking.total_price)}</span>
                  </div>
                  {booking.modification_fee > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-yellow-400">{t('travelerDash.card.modFee')}</span>
                      <span className="text-yellow-400 font-mono">{formatYen(booking.modification_fee)}</span>
                    </div>
                  )}
                  {booking.cancellation_fee > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-red-400">{t('travelerDash.card.cancelFee')}</span>
                      <span className="text-red-400 font-mono">{formatYen(booking.cancellation_fee)}</span>
                    </div>
                  )}
                  {booking.refund_amount > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-green-400">{t('travelerDash.card.refund')}</span>
                      <span className="text-green-400 font-mono">{formatYen(booking.refund_amount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Guide info */}
            {booking.guide_name && (
              <div>
                <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                  <User className="h-4 w-4 text-[#00F0FF]" /> {t('travelerDash.card.host')}
                </h4>
                <p className="text-zinc-300">{booking.guide_name}</p>
              </div>
            )}

            {/* Historique des actions */}
            {historyRows.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-zinc-400" /> {t('travelerDash.card.history')}
                </h4>
                <div className="bg-zinc-800/30 rounded-lg p-4">{historyRows}</div>
              </div>
            )}

            {/* Special requests */}
            {booking.special_requests && (
              <div>
                <h4 className="text-sm font-bold text-white mb-2">{t('travelerDash.card.specialRequests')}</h4>
                <p className="text-zinc-400 text-sm">{booking.special_requests}</p>
              </div>
            )}

            <div className="text-xs text-zinc-600">
              {t('travelerDash.card.createdOn', { date: booking.created_at ? new Date(booking.created_at).toLocaleString(dateLocale) : 'N/A' })}
              {booking.updated_at && t('travelerDash.card.modifiedOn', { date: new Date(booking.updated_at).toLocaleString(dateLocale) })}
            </div>
          </div>
        )}
      </Card>

      {showChat && <ChatModal booking={booking} onClose={function() { setShowChat(false); }} />}
      {showCancel && <CancelModal booking={booking} onClose={function() { setShowCancel(false); }} onConfirmed={function() { setShowCancel(false); onRefresh(); }} />}
      {showModify && <ModifyModal booking={booking} onClose={function() { setShowModify(false); }} onConfirmed={function() { setShowModify(false); onRefresh(); }} />}
    </div>
  );
}

/* ========== MAIN DASHBOARD ========== */
export default function Dashboard() {
  useCurrency(); // re-render quand la devise d'affichage change
  var { t } = useTranslation();
  var navigate = useNavigate();
  var auth = useAuth();
  var user = auth.user;
  var loading = auth.loading;
  var [searchParams] = useSearchParams();
  var [isTestingView, setIsTestingView] = React.useState(false);
  var [bookings, setBookings] = React.useState([]);
  var [loadingBookings, setLoadingBookings] = React.useState(true);
  var [filter, setFilter] = React.useState('all');

  React.useEffect(function() {
    var viewAs = searchParams.get('viewAs');
    if (viewAs === 'traveler') { setIsTestingView(true); return; }
    var testingAsRole = sessionStorage.getItem('testingAsRole');
    if (testingAsRole === 'traveler') { setIsTestingView(true); sessionStorage.removeItem('testingAsRole'); return; }

    if (!loading && user) {
      if (user.role === 'admin' && !viewAs) { navigate('/admin/dashboard', { replace: true }); }
      else if (user.role === 'guide') { navigate('/guide/dashboard', { replace: true }); }
    }
  }, [loading, user, navigate, searchParams]);

  React.useEffect(function() {
    // Clear return URL after login
    sessionStorage.removeItem('returnAfterLogin');
    fetchBookings();
  }, []);

  function fetchBookings() {
    setLoadingBookings(true);
    axios.get(API_URL + '/api/bookings/v2/me/traveler', { withCredentials: true })
      .then(function(res) { setBookings(res.data); setLoadingBookings(false); })
      .catch(function() { setLoadingBookings(false); });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" />
      </div>
    );
  }

  var filteredBookings = [];
  for (var i = 0; i < bookings.length; i++) {
    var b = bookings[i];
    var done = isBookingCompleted(b);
    var isActive = (b.booking_status === 'confirmed' || b.booking_status === 'modified') && !done;
    if (filter === 'all') { filteredBookings.push(b); }
    else if (filter === 'active' && isActive) { filteredBookings.push(b); }
    else if (filter === 'completed' && done) { filteredBookings.push(b); }
    else if (filter === 'cancelled' && b.booking_status === 'cancelled') { filteredBookings.push(b); }
    else if (filter === 'pending' && b.booking_status === 'pending') { filteredBookings.push(b); }
  }

  var stats = { total: bookings.length, active: 0, completed: 0, cancelled: 0, pending: 0, totalSpent: 0 };
  for (var j = 0; j < bookings.length; j++) {
    var bkDone = isBookingCompleted(bookings[j]);
    if ((bookings[j].booking_status === 'confirmed' || bookings[j].booking_status === 'modified') && bkDone) stats.completed++;
    else if (bookings[j].booking_status === 'confirmed' || bookings[j].booking_status === 'modified') stats.active++;
    else if (bookings[j].booking_status === 'cancelled') stats.cancelled++;
    else if (bookings[j].booking_status === 'pending') stats.pending++;
    // Net réellement dépensé : payé moins remboursé.
    if (bookings[j].payment_status === 'paid') stats.totalSpent += (bookings[j].total_price || 0) - (bookings[j].refund_amount || 0);
  }

  var bookingCards = [];
  for (var k = 0; k < filteredBookings.length; k++) {
    bookingCards.push(<BookingDetailCard key={filteredBookings[k].booking_id} booking={filteredBookings[k]} onRefresh={fetchBookings} />);
  }

  // Historique des paiements : reconstruit le grand livre (paiements + remboursements)
  // à partir de la réservation et de son historique d'actions.
  var payEvents = [];
  for (var pe = 0; pe < bookings.length; pe++) {
    var bk = bookings[pe];
    if (bk.payment_status !== 'paid') continue;
    var bkName = bk.activity_name && typeof bk.activity_name === 'object' ? (bk.activity_name.fr || bk.activity_name.en) : (bk.activity_name || '');
    var modSuffix = ' (' + t('travelerDash.payHistory.modification') + ')';
    var hist = bk.action_history || [];

    // Montant réellement payé au checkout = prix avant la 1re modification
    // (sinon total_price a déjà changé). À défaut, total_price courant.
    var initialPaid = null;
    for (var mi = 0; mi < hist.length; mi++) {
      if (hist[mi].action === 'modification' && typeof hist[mi].original_price === 'number') { initialPaid = hist[mi].original_price; break; }
    }
    if (initialPaid === null) initialPaid = bk.total_price || 0;
    payEvents.push({ type: 'payment', date: bk.created_at, label: bkName, amount: initialPaid });

    var histRefunds = 0;
    for (var he = 0; he < hist.length; he++) {
      var ev = hist[he];
      if (ev.action === 'modification') {
        var fee = ev.modification_fee || 0;
        var pdiff = (typeof ev.price_difference === 'number') ? ev.price_difference : ((ev.new_price || 0) - (ev.original_price || 0));
        if (pdiff > 0) {
          payEvents.push({ type: 'payment', date: ev.timestamp, label: bkName + ' — ' + t('travelerDash.payHistory.supplement'), amount: pdiff });
        } else if (pdiff < 0) {
          histRefunds += -pdiff;
          payEvents.push({ type: 'refund', date: ev.timestamp, label: bkName + modSuffix, amount: -pdiff });
        }
        if (fee > 0) {
          payEvents.push({ type: 'payment', date: ev.timestamp, label: bkName + ' — ' + t('travelerDash.payHistory.modFee'), amount: fee });
        }
        continue;
      }
      var refAmt = ev.refund_amount || (ev.action === 'admin_refund' ? ev.amount : 0) || 0;
      if (refAmt > 0) {
        histRefunds += refAmt;
        payEvents.push({ type: 'refund', date: ev.timestamp || ev.at || bk.updated_at, label: bkName, amount: refAmt, reason: ev.reason || '' });
      }
    }
    // Remboursement enregistré sur la résa mais absent de l'historique (filet).
    var extraRefund = (bk.refund_amount || 0) - histRefunds;
    if (extraRefund > 0) {
      payEvents.push({ type: 'refund', date: bk.cancelled_at || bk.updated_at || bk.created_at, label: bkName, amount: extraRefund });
    }
  }
  payEvents.sort(function(a, b) { return (b.date || '').localeCompare(a.date || ''); });

  var filterDefs = [
    { key: 'all', count: bookings.length },
    { key: 'active', count: stats.active },
    { key: 'completed', count: stats.completed },
    { key: 'pending', count: stats.pending },
    { key: 'cancelled', count: stats.cancelled }
  ];

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="traveler-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Admin Testing Banner */}
        {isTestingView && user?.role === 'admin' && (
          <div className="mb-6 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-[#00F0FF] font-bold">{t('travelerDash.testMode')}</span>
              <span className="text-zinc-300">{t('travelerDash.travelerView')}</span>
            </div>
            <Button onClick={function() { navigate('/admin/dashboard'); }} variant="outline" className="border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10">
              <ArrowLeft className="h-4 w-4 mr-2" /> {t('travelerDash.backAdmin')}
            </Button>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            {t('travelerDash.mySpace')}
          </h1>
          <p className="text-zinc-400 mt-1">{t('travelerDash.welcome', { name: user?.name })}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="bg-zinc-900/80 p-4 border border-white/5">
            <p className="text-zinc-500 text-xs">{t('travelerDash.stats.bookings')}</p>
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.total}</p>
          </Card>
          <Card className="bg-zinc-900/80 p-4 border border-white/5">
            <p className="text-zinc-500 text-xs">{t('travelerDash.stats.active')}</p>
            <p className="text-2xl font-bold text-green-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.active}</p>
          </Card>
          <Card className="bg-zinc-900/80 p-4 border border-white/5">
            <p className="text-zinc-500 text-xs">{t('travelerDash.stats.pending')}</p>
            <p className="text-2xl font-bold text-yellow-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stats.pending}</p>
          </Card>
          <Card className="bg-zinc-900/80 p-4 border border-white/5">
            <p className="text-zinc-500 text-xs">{t('travelerDash.stats.spent')}</p>
            <p className="text-2xl font-bold text-[#F9004D]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{formatYen(stats.totalSpent)}</p>
          </Card>
        </div>

        {/* Passeport Tokyo */}
        <div className="mb-8">
          <PassportWidget />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {filterDefs.map(function(f) {
            return (
              <Button key={f.key} onClick={function() { setFilter(f.key); }} variant={filter === f.key ? 'default' : 'outline'}
                size="sm" className={filter === f.key ? 'bg-[#F9004D] text-white' : 'border-white/20 text-zinc-400 hover:text-white'}
                data-testid={'filter-' + f.key}>
                {t('travelerDash.filters.' + f.key) + ' (' + f.count + ')'}
              </Button>
            );
          })}
        </div>

        {/* Bookings */}
        {loadingBookings ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 text-[#F9004D] animate-spin" /></div>
        ) : filteredBookings.length === 0 ? (
          <Card className="bg-zinc-900/50 border-white/10 p-12 text-center">
            <Calendar className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">{t('travelerDash.empty.title')}</h3>
            <p className="text-zinc-400 mb-6">{t('travelerDash.empty.desc')}</p>
            <Button onClick={function() { navigate('/activities'); }} className="bg-[#F9004D] hover:bg-[#D00040] text-white" data-testid="explore-activities-btn">
              {t('travelerDash.empty.cta')}
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">{bookingCards}</div>
        )}

        {/* Historique des paiements */}
        {payEvents.length > 0 && (
          <Card className="bg-zinc-900/50 border-white/10 p-6 mt-8" data-testid="payment-history">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#F9004D]" /> {t('travelerDash.payHistory.title')}
            </h3>
            <div className="space-y-1">
              {payEvents.map(function(ev, ei) {
                var isRefund = ev.type === 'refund';
                return (
                  <div key={ei} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 gap-3">
                    <div className="min-w-0">
                      <span className={'text-sm font-semibold ' + (isRefund ? 'text-green-400' : 'text-white')}>
                        {isRefund ? t('travelerDash.payHistory.refund') : t('travelerDash.payHistory.payment')}
                      </span>
                      <span className="text-zinc-500 text-sm"> · {ev.label}</span>
                      {ev.reason && <p className="text-zinc-500 text-xs truncate">{ev.reason}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={'font-mono font-bold text-sm ' + (isRefund ? 'text-green-400' : 'text-white')}>
                        {(isRefund ? '+' : '−') + formatYen(ev.amount)}
                      </p>
                      <p className="text-zinc-600 text-xs">{ev.date ? new Date(ev.date).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
